import CustomTable from "@/components/CustomTable";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchIcon } from "@/assets";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getWallexRates } from "@/services/Rates.service";
import { wallexRateColumns, type WallexRate } from "./columns";

const parseRateValue = (
  value: number | string | undefined | null
): number | null => {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const WallexRates = () => {
  const [search, setSearch] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState<string>("all");

  const wallexRatesQuery = useQuery({
    queryKey: ["wallexRates"],
    queryFn: getWallexRates,
  });

  const normalizedRates = useMemo<WallexRate[]>(() => {
    const payload = wallexRatesQuery.data?.data?.data;
    if (!Array.isArray(payload)) {
      return [];
    }
    return payload.map((rate) => {
      const currency =
        rate.base && rate.quote
          ? `${rate.base}/${rate.quote}`
          : rate.ticker ?? "Unknown pair";

      return {
        id: rate.ticker ?? `${rate.base}${rate.quote}`,
        currency,
        buyingRate: parseRateValue(rate.buy),
        sellingRate: parseRateValue(rate.sell),
      };
    });
  }, [wallexRatesQuery.data]);

  const currencyOptions = useMemo(() => {
    const options = new Set<string>();
    normalizedRates.forEach((row) => {
      const [base, quote] = row.currency.split("/");
      if (base && quote) {
        options.add(base);
      }
    });
    return Array.from(options);
  }, [normalizedRates]);

  useEffect(() => {
    if (
      currencyFilter !== "all" &&
      !currencyOptions.includes(currencyFilter)
    ) {
      setCurrencyFilter("all");
    }
  }, [currencyFilter, currencyOptions]);

  const filteredData = useMemo(() => {
    return normalizedRates.filter((row) => {
      const matchesSearch =
        !search ||
        row.currency.toLowerCase().includes(search.toLowerCase());
      const matchesCurrency =
        currencyFilter === "all" || row.currency.startsWith(currencyFilter);
      return matchesSearch && matchesCurrency;
    });
  }, [normalizedRates, search, currencyFilter]);

  return (
    <div className="grid gap-8 p-6 py-12">
      <div>
        <p className="text-2xl font-medium text-primary-3">Wallex Rates</p>
      </div>

      <div className="rounded-[12px] border border-[#EAE6EA] bg-card shadow-sm p-6 flex flex-col gap-6">
        <h3 className="text-base font-semibold text-primary-3">
          Current exchange rates
        </h3>

        <div className="flex flex-wrap items-end gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-70">
              <SearchIcon />
            </span>
            <Input
              placeholder="Search currency pair"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 border-grey-300 rounded-[8px] bg-off-white-1"
            />
          </div>

          <div className="flex items-end gap-4 flex-wrap">
            <div className="flex flex-col gap-1.5 min-w-[120px]">
              <label className="text-xs text-gray-80">Currency</label>
              <Select value={currencyFilter} onValueChange={setCurrencyFilter}>
                <SelectTrigger className="h-10 border-grey-300 rounded-[8px] px-3 w-[120px]">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {currencyOptions.map((currency) => (
                    <SelectItem key={currency} value={currency}>
                      {currency}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="border rounded-md overflow-hidden">
          <CustomTable
            columns={wallexRateColumns}
            data={filteredData}
            isLoading={wallexRatesQuery.isLoading}
          />
        </div>
        {wallexRatesQuery.isError && (
          <p className="text-sm text-red-500 px-4 py-3">
            Failed to load Wallex rates.
          </p>
        )}
      </div>
    </div>
  );
};

export default WallexRates;
